export enum FrameKind {
  ArtifactChunk = "artifact_chunk",
  ArtifactEnd = "artifact_end",
  ArtifactStart = "artifact_start",
  AvailableCommands = "available_commands",
  BotActionResult = "bot_action_result",
  BotConfirmation = "bot_confirmation",
  BotMedia = "bot_media",
  BotCitation = "bot_citation",
  BotCitations = "bot_citations",
  BotMessageChunk = "bot_message_chunk",
  BotMessageComplete = "bot_message_complete",
  BotNarrationPromote = "bot_narration_promote",
  BotRefusal = "bot_refusal",
  BotThinkingChunk = "bot_thinking_chunk",
  ThinkingComplete = "thinking_complete",
  BotToolProgress = "bot_tool_progress",
  BotToolStatus = "bot_tool_status",
  Closed = "closed",
  Connected = "connected",
  DocumentIngest = "document_ingest",
  // DocumentIngested: legacy/back-compat — keep parsing but the backend
  // publishes "document_ingest" (singular). Both map onto the same reducer
  // action so either wire shape works.
  DocumentIngested = "document_ingested",
  Error = "error",
  FactProposal = "fact_proposal",
  FactsUpdated = "facts_updated",
  PreparingResponse = "preparing_response",
  // Agent-loop budget/step telemetry (Task 6/7). These arrive flat off the
  // wire — the loop yields {"kind": "iteration_start", ...} directly and the
  // route passes it through via `_sse(ev)`, with no type/payload envelope
  // like most other frames.
  IterationStart = "iteration_start",
  IterationEnd = "iteration_end",
  BudgetWarning = "budget_warning",
  BudgetPaused = "budget_paused",
  // Blocking plan mode. PlanUpdated: advisory write_todos snapshot (the live
  // plan). PlanProposed: the turn ended awaiting the user's approve/edit/reject
  // on the plan before any execution runs.
  PlanUpdated = "plan_updated",
  PlanProposed = "plan_proposed",
  SkillProposal = "skill_proposal",
  // Task 8: a mid-turn user message injected into the live loop (Task 3),
  // surfaced so the client renders it inline. Not a terminal frame.
  SteerApplied = "steer_applied",
  // Refusal: legacy alias for BotRefusal. Backend emits bot_refusal.
  Refusal = "refusal",
  // Tool approval gate: the turn ENDED at a gated tool (send_email) awaiting
  // the user's approve/reject. Without this member the frame parsed to Unknown
  // and was dropped, so a gated send ended the turn with nothing rendered.
  ReviewRequired = "review_required",
  Thinking = "thinking",
  // ToolStart/End/Progress: legacy aliases for BotTool*. Backend emits
  // bot_tool_status (with payload.phase) and bot_tool_progress today.
  // Kept in the enum so legacy tests and any external producer still parse.
  ToolEnd = "tool_end",
  ToolProgress = "tool_progress",
  ToolStart = "tool_start",
  SubagentEvent = "subagent_event",
  TurnMetrics = "turn_metrics",
  WebSearchResult = "web_search_result",
  // Synthetic, client-only. The hook dispatches this when the SSE stream
  // ends without a terminal frame (network drop, backend crash, proxy
  // timeout) so the turn is finalized instead of hanging in Streaming
  // forever. The backend never emits it.
  StreamClosed = "__stream_closed__",
  Unknown = "__unknown__",
}

// The Citation type is canonical in ./types (it predates this frame work).
// Re-export it here so frame/reducer code can import it from ./events as the
// plan specified, without a duplicate declaration (which would clash on the
// barrel re-export in ./index).
export type { Citation } from "./types";
import type { Citation } from "./types";

export interface BaseFrame {
  kind: FrameKind;
  [key: string]: unknown;
}

export interface ConnectedFrame extends BaseFrame {
  kind: FrameKind.Connected;
  session_id?: string;
}
export interface PreparingResponseFrame extends BaseFrame {
  kind: FrameKind.PreparingResponse;
}

/** SP-3b: one user-invokable script from an active v2 plugin. The
 *  resolver has already filtered these by script_roles, so anything in
 *  this list is allowed for the current user. */
export interface AvailableCommandEntry {
  plugin: string;
  name: string;
  /** "<plugin>:<name>" — stable id the UI uses to key the command list. */
  namespaced_name: string;
  title: string;
  description: string;
  /** The markdown body the UI sends as the next user message when the
   *  user picks this command. */
  prompt_template: string;
}
export interface AvailableCommandsFrame extends BaseFrame {
  kind: FrameKind.AvailableCommands;
  commands: AvailableCommandEntry[];
}
export interface BotChunkFrame extends BaseFrame {
  kind: FrameKind.BotMessageChunk;
  text: string;
  message_id?: string;
}
export interface BotCompleteFrame extends BaseFrame {
  kind: FrameKind.BotMessageComplete;
  message_id?: string;
}
/** Client-only synthetic frame (see FrameKind.StreamClosed). Dispatched by the
 *  streaming hook when the SSE stream ends or stalls without a terminal frame. */
export interface StreamClosedFrame extends BaseFrame {
  kind: FrameKind.StreamClosed;
}
export interface BotThinkingChunkFrame extends BaseFrame {
  kind: FrameKind.BotThinkingChunk;
  text: string;
}
export interface ThinkingCompleteFrame extends BaseFrame {
  kind: FrameKind.ThinkingComplete;
  thinking_ms: number;
}
// Legacy tool envelopes — kept for parsing.
export interface ToolStartFrame extends BaseFrame {
  kind: FrameKind.ToolStart;
  tool_name: string;
  tool_call_id?: string;
}
export interface ToolEndFrame extends BaseFrame {
  kind: FrameKind.ToolEnd;
  tool_name: string;
  result?: unknown;
}
// Live wire format. bot_tool_status carries payload.phase = "start" | "end"
// (start/end share an envelope). bot_tool_progress carries progress info OR
// a heartbeat flag for tools that don't self-report.
export interface BotToolStatusFrame extends BaseFrame {
  kind: FrameKind.BotToolStatus;
  phase: "start" | "end";
  tool: string;
  label?: string;
  is_error?: boolean;
  latency_ms?: number;
}
export interface BotToolProgressFrame extends BaseFrame {
  kind: FrameKind.BotToolProgress;
  tool: string;
  tool_call_id?: string;
  progress?: number | null;
  total?: number | null;
  message?: string | null;
  iteration?: number;
  /** Synthetic heartbeat for a long-running silent tool — distinct from
   *  real progress reports so the UI can render differently if it wants. */
  heartbeat?: boolean;
}
// Legacy alias.
export interface ToolProgressFrame extends BaseFrame {
  kind: FrameKind.ToolProgress;
  tool_name: string;
  text?: string;
}
export interface BotCitationFrame extends BaseFrame {
  kind: FrameKind.BotCitation;
  url?: string;
  title?: string;
  snippet?: string;
}
export interface BotCitationsFrame extends BaseFrame {
  kind: FrameKind.BotCitations;
  citations: Citation[];
}

/** New accumulation frame: the backend emits one per web search result batch.
 *  Unlike bot_citations (which replaces), these are appended onto the turn's
 *  citations and deduped by url. */
export interface WebSearchResultFrame extends BaseFrame {
  kind: FrameKind.WebSearchResult;
  citations: Citation[];
}
export interface BotRefusalFrame extends BaseFrame {
  kind: FrameKind.BotRefusal;
  stop_reason?: string;
  text?: string;
}
// Legacy alias.
export interface RefusalFrame extends BaseFrame {
  kind: FrameKind.Refusal;
  reason?: string;
}
export interface FactsUpdatedFrame extends BaseFrame {
  kind: FrameKind.FactsUpdated;
}

/** A pending proposal the user can accept/reject. Both shapes mirror the
 *  backend's _translate_*_proposal payloads. UI rendering lives in
 *  Slice 2 (/workspace/proposals); the parser support lands here so the
 *  frames stop reading as Unknown in diagnostics. */
export interface SkillProposalFrame extends BaseFrame {
  kind: FrameKind.SkillProposal;
  proposal_id: string;
  name: string;
  description: string;
  body: string;
  proposed_scope: string;
  trigger_kind: string;
}
export interface FactProposalFrame extends BaseFrame {
  kind: FrameKind.FactProposal;
  proposal_id: string;
  text: string;
  category: string;
  evidence: string;
  trigger_kind: string;
}

/** A mid-turn user message that was injected into the live agent loop
 *  (Task 3) and echoed back over the stream. The client renders it as a
 *  user message in the timeline; the turn keeps streaming. The parser
 *  hoists payload.text to the top level, so `text` reads flat. */
export interface SteerAppliedFrame extends BaseFrame {
  kind: FrameKind.SteerApplied;
  session_id?: string;
  message_id?: string;
  text: string;
}

/** Confirmation envelope for mutating tool calls — `<confirm>` blocks
 *  the model emits. The summary is what the UI shows the user before
 *  the action runs. */
export interface BotConfirmationFrame extends BaseFrame {
  kind: FrameKind.BotConfirmation;
  action: string;
  summary: string;
  params?: Record<string, unknown>;
}

/** Promoted narration text — the model's pre-tool-use prose that the
 *  backend lifts out of the assistant message so the UI can render it
 *  as a distinct kind. */
export interface BotNarrationPromoteFrame extends BaseFrame {
  kind: FrameKind.BotNarrationPromote;
  text: string;
}

/** One todo in a plan (write_todos). Mirrors the backend's normalized shape. */
export interface PlanTodo {
  id: string;
  content: string;
  status: "pending" | "in_progress" | "completed";
}

/** Advisory plan snapshot — the model's current write_todos list. Replaces
 *  the prior snapshot each emit. Non-terminal; the turn keeps streaming. */
export interface PlanUpdatedFrame extends BaseFrame {
  kind: FrameKind.PlanUpdated;
  todos: PlanTodo[];
}

/** Blocking plan mode: the turn ended awaiting the user's decision on the
 *  proposed plan. The client renders an approve/edit/reject card and POSTs a
 *  plan_decision to /steer to resume. */
export interface PlanProposedFrame extends BaseFrame {
  kind: FrameKind.PlanProposed;
  plan_id: string;
  todos: PlanTodo[];
  awaiting_approval?: boolean;
}

/** Tool approval gate: the agent loop reached a tool in the approval allowlist
 *  (`UNIFIED_AGENT_TOOL_APPROVAL_TOOLS`), persisted the pending call and ENDED
 *  the turn. Nothing runs until the client POSTs a tool_decision to /steer —
 *  and even then the server only consumes it at the top of the NEXT turn.
 *
 *  `error` is set INSTEAD of `args` when the gate could not persist the pending
 *  call (`approval_unavailable`). The gate is fail-CLOSED, so that means the
 *  tool did NOT run and there is nothing left to approve. */
export interface ReviewRequiredFrame extends BaseFrame {
  kind: FrameKind.ReviewRequired;
  /** Identifies the pending call; must be echoed in the decision. */
  call_id: string;
  tool_name: string;
  /** Human-readable rendering of the call — what to show the user. */
  summary?: string | null;
  /** The arguments as the model proposed them. Display only: the decision
   *  cannot change them (spec §5.3 — no `edit`). */
  args?: Record<string, unknown> | null;
  /** Set when the gate itself failed; the action did not run. */
  error?: string | null;
  /** `send_email` only (spec §3): who the server thinks each recipient is.
   *  ABSENT — never empty, never defaulted — when the gate could not resolve
   *  them (no company repo, no company, lookup raised), which is why this is
   *  optional rather than nullable: absent means "nobody checked", and the
   *  card shows the plain addresses instead of inventing a verdict. */
  recipients?: RecipientVerdict[];
  /** `send_email` only (T15c): which FILES the call will attach, named.
   *  ABSENT — never empty, never defaulted — when the gate could not describe
   *  them (no resolver, no conversation, lookup raised), so the card falls
   *  back to counting them. One entry per attachment, in the order the send
   *  will use. */
  attachments?: AttachmentPreview[];
  /** `promote_to_knowledge` only (spec 2026-10-05 §3.4): the file the call
   *  would add, ABSENT when the server did not name it. */
  document?: PromoteDocumentPreview;
}

/** The document a gated `promote_to_knowledge` would add, as the server named
 *  it — only after the promote's own write gate passed. */
export interface PromoteDocumentPreview {
  id: number;
  filename: string;
  status: string;
}

/** One file a gated `send_email` proposes to attach, as the SERVER sees it.
 *
 *  The name comes off the document row only after the send's own three gates
 *  pass (`EmailAttachmentResolver.describe`), because a filename is
 *  information: "salary-review.pdf" can be the whole disclosure. Exactly one
 *  of `filename` and `error` is set — an entry the server would not name
 *  carries the refusal code the send would raise instead, and is never
 *  dropped, since T10 refuses the WHOLE send on one bad id. */
export interface AttachmentPreview {
  document_id: string;
  filename?: string;
  error?: string;
}

/** What the server established about one would-be recipient. Mirrors
 *  `infra.email.recipient_resolver.RecipientStatus`.
 *
 *  No display name: `egpt_company_members` has no name column (spec §3's
 *  recorded gap), so even a `known_member` can only be shown as an address.
 *  `suggestion` is present only for a `likely_typo` that had a close enough
 *  colleague to name — and it is information, not an action: with `edit`
 *  dropped (§5.3) the only correction is reject and re-prompt. */
export interface RecipientVerdict {
  email: string;
  status: "known_member" | "likely_typo" | "unrecognized_external" | "unrecognized";
  suggestion?: string;
}

/** Outcome marker after a mutating tool finishes. Lets the UI render
 *  a confirmation pill ("✓ Renamed role") distinct from the raw
 *  bot_tool_status end-phase chip. */
export interface BotActionResultFrame extends BaseFrame {
  kind: FrameKind.BotActionResult;
  action: string;
  status: "succeeded" | "failed";
  latency_ms?: number | null;
}
export interface DocumentIngestFrame extends BaseFrame {
  kind: FrameKind.DocumentIngest | FrameKind.DocumentIngested;
  document_id?: number;
  filename?: string;
  scope?: string;
  status?: "processing" | "ready" | "failed";
  error?: string;
}
export interface TurnMetricsFrame extends BaseFrame {
  kind: FrameKind.TurnMetrics;
  ttft_ms?: number;
  total_latency_ms?: number;
  cost_usd?: number;
  input_tokens?: number;
  cache_read_input_tokens?: number;
  model?: string;
}
export interface ErrorFrame extends BaseFrame {
  kind: FrameKind.Error;
  message: string;
  retryable?: boolean;
  /** Server-side classification. `policy_blocked` / `policy_unavailable` mark
   *  a governance DECISION rather than a fault — see `isPolicyDecision`. */
  code?: string;
}
export interface ClosedFrame extends BaseFrame {
  kind: FrameKind.Closed;
}
export interface UnknownFrame extends BaseFrame {
  kind: FrameKind.Unknown;
  raw: unknown;
}

export type ArtifactKind = "code" | "html" | "table" | "file" | "document";
export type MediaKind = "image" | "audio" | "video";

export interface BotMediaFrame extends BaseFrame {
  kind: FrameKind.BotMedia;
  artifact_id: string;
  media_kind: MediaKind;
  mime: string;
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  prompt: string | null;
}

export interface ArtifactStartFrame extends BaseFrame {
  kind: FrameKind.ArtifactStart;
  artifact_id: string;
  artifact_kind: ArtifactKind;
  title: string | null;
  mime: string | null;
  language: string | null;
  position_hint: number;
  review_required?: boolean;
  /** Card display hint; "export_only" means no expand (ELASV1-3518). */
  display?: "export_only";
  /** An export-only artifact's body length. Its body is never streamed (no
   *  artifact_chunk follows); Export reads it by id (ELASV1-3514). */
  size?: number;
  /** The formats the card's Export offers; absent = the kind's menu. */
  export_formats?: string[];
  /** An export-only table's bounded read-only preview. */
  preview?: import("./types").ArtifactTablePreview;
}

export interface ArtifactChunkFrame extends BaseFrame {
  kind: FrameKind.ArtifactChunk;
  artifact_id: string;
  text: string;
}

export interface ArtifactEndFrame extends BaseFrame {
  kind: FrameKind.ArtifactEnd;
  artifact_id: string;
  final_length: number;
  status: "succeeded" | "failed";
  error: string | null;
}

/** Per-tool-loop-iteration budget sub-object carried on iteration_end. */
export interface IterationBudget {
  max_turn_cost_usd: number;
  max_turn_seconds: number;
  max_tool_iterations: number;
}

/** Emitted at the start of each agent-loop tool-iteration. */
export interface IterationStartFrame extends BaseFrame {
  kind: FrameKind.IterationStart;
  iteration: number;
  model: string;
  n_tools: number;
  tool_names: string[];
}

/** Emitted at the end of each agent-loop tool-iteration with the per-iteration
 *  cost/usage and a budget snapshot. */
export interface IterationEndFrame extends BaseFrame {
  kind: FrameKind.IterationEnd;
  iteration: number;
  stop_reason?: string | null;
  input_tokens?: number;
  output_tokens?: number;
  cache_read_tokens?: number;
  cache_write_tokens?: number;
  iter_cost_usd?: number;
  cumulative_cost_usd?: number;
  elapsed_s?: number;
  budget?: IterationBudget;
}

/** Soft warning fired once per dimension when usage crosses warn_fraction but
 *  is still under the cap. Non-terminal — the loop keeps running. */
export interface BudgetWarningFrame extends BaseFrame {
  kind: FrameKind.BudgetWarning;
  dimension: "cost" | "time";
  used_fraction: number;
  cumulative_cost_usd: number;
  elapsed_s: number;
}

/** The turn was paused because a budget dimension was exceeded. Resumable via
 *  a budget_decision steer. */
export interface BudgetPausedFrame extends BaseFrame {
  kind: FrameKind.BudgetPaused;
  dimension: "cost" | "time" | "iterations";
  reason: string;
  resume_token?: string;
  cumulative_cost_usd?: number;
  elapsed_s?: number;
  iteration?: number;
}

export interface SubagentEventFrame extends BaseFrame {
  kind: FrameKind.SubagentEvent;
  job_id: string;
  seq: number;
  event_kind:
    | "start"
    | "tool_use"
    | "tool_result"
    | "thinking"
    | "progress"
    | "done"
    | "error";
  data: any;
  ts: string;
}

export type Frame =
  | ConnectedFrame
  | PreparingResponseFrame
  | AvailableCommandsFrame
  | BotChunkFrame
  | BotCompleteFrame
  | BotThinkingChunkFrame
  | ThinkingCompleteFrame
  | ToolStartFrame
  | ToolEndFrame
  | ToolProgressFrame
  | BotToolStatusFrame
  | BotToolProgressFrame
  | BotCitationFrame
  | BotCitationsFrame
  | BotRefusalFrame
  | RefusalFrame
  | FactsUpdatedFrame
  | SkillProposalFrame
  | SteerAppliedFrame
  | FactProposalFrame
  | BotConfirmationFrame
  | BotNarrationPromoteFrame
  | PlanUpdatedFrame
  | PlanProposedFrame
  | ReviewRequiredFrame
  | BotActionResultFrame
  | DocumentIngestFrame
  | TurnMetricsFrame
  | ErrorFrame
  | ClosedFrame
  | UnknownFrame
  | ArtifactStartFrame
  | ArtifactChunkFrame
  | ArtifactEndFrame
  | BotMediaFrame
  | SubagentEventFrame
  | StreamClosedFrame
  | WebSearchResultFrame
  | IterationStartFrame
  | IterationEndFrame
  | BudgetWarningFrame
  | BudgetPausedFrame;

const KIND_VALUES = new Set<string>(Object.values(FrameKind));

/**
 * Parse a raw SSE-decoded object into a typed Frame.
 *
 * The unified_agent wire format uses `type` as the discriminator and
 * nests most variant-specific fields under `payload`. We normalize on
 * read so the rest of the codebase sees a flat frame keyed by `kind`.
 *
 * Returns a single Frame for most cases; bot_message_chunk with
 * payload.done=true is handled by emitting just the chunk here — the
 * reducer treats `payload.done` separately via parseFramesFromRaw.
 */
export function parseFrame(raw: unknown): Frame {
  if (!raw || typeof raw !== "object") {
    return { kind: FrameKind.Unknown, raw } as UnknownFrame;
  }
  const obj = raw as Record<string, unknown>;
  const t = obj.type ?? obj.kind; // tolerate both during transition
  if (typeof t !== "string") {
    return { kind: FrameKind.Unknown, raw } as UnknownFrame;
  }
  if (!KIND_VALUES.has(t) || t === FrameKind.Unknown) {
    return { kind: FrameKind.Unknown, raw } as UnknownFrame;
  }
  // Hoist payload keys to the top level so the existing guards/reducer
  // see frame.text, frame.tool_name, etc.
  let payload =
    obj.payload && typeof obj.payload === "object"
      ? (obj.payload as Record<string, unknown>)
      : {};

  // artifact_start ships payload.kind which clashes with our frame-level kind discriminator.
  // Rename to artifact_kind during normalisation.
  if (t === FrameKind.ArtifactStart && "kind" in payload) {
    payload = { ...payload, artifact_kind: payload.kind };
    delete (payload as any).kind;
  }
  // bot_media ships payload.kind (the media kind: image/audio/video) which clashes with
  // the frame-level kind discriminator. Rename to media_kind during normalisation.
  if (t === FrameKind.BotMedia && "kind" in payload) {
    payload = { ...payload, media_kind: payload.kind };
    delete (payload as any).kind;
  }
  // subagent_event ships payload.kind (the event kind: tool_use/done/etc.) which clashes
  // with the frame-level kind discriminator. Rename to event_kind during normalisation.
  if (t === FrameKind.SubagentEvent && "kind" in payload) {
    payload = { ...payload, event_kind: payload.kind };
    delete (payload as any).kind;
  }

  const flat: Record<string, unknown> = { ...obj, ...payload, kind: t };
  delete (flat as any).type; // we already moved it into kind
  delete (flat as any).payload; // hoisted
  return flat as Frame;
}

/**
 * Parse one raw event into one or more Frames. Used by parseSSEStream
 * to handle the bot_message_chunk-with-done case: the backend doesn't
 * emit a separate bot_message_complete frame, so we synthesize one.
 */
export function parseFramesFromRaw(raw: unknown): Frame[] {
  const f = parseFrame(raw);
  if (f.kind === FrameKind.BotMessageChunk && (f as any).done === true) {
    const completeFrame: Frame = {
      kind: FrameKind.BotMessageComplete,
      message_id: (f as any).message_id,
    } as Frame;
    return [f, completeFrame];
  }
  return [f];
}

export const isBotChunk = (f: Frame): f is BotChunkFrame =>
  f.kind === FrameKind.BotMessageChunk;
export const isBotComplete = (f: Frame): f is BotCompleteFrame =>
  f.kind === FrameKind.BotMessageComplete;
// Tool guards accept both legacy and live envelope shapes. The live wire is
// bot_tool_status (with payload.phase); legacy tests still emit tool_start /
// tool_end as standalone kinds. Callers reach for tool name via the helper
// below so they don't have to know which shape they got.
export const isToolStart = (f: Frame): boolean =>
  f.kind === FrameKind.ToolStart ||
  (f.kind === FrameKind.BotToolStatus &&
    (f as BotToolStatusFrame).phase === "start");
export const isToolEnd = (f: Frame): boolean =>
  f.kind === FrameKind.ToolEnd ||
  (f.kind === FrameKind.BotToolStatus &&
    (f as BotToolStatusFrame).phase === "end");
export const isToolProgress = (f: Frame): boolean =>
  f.kind === FrameKind.ToolProgress || f.kind === FrameKind.BotToolProgress;
export const isError = (f: Frame): f is ErrorFrame =>
  f.kind === FrameKind.Error;
/** An error frame the deployment MEANT to send: the turn was refused by an
 *  organisation policy, or refused because the policy store could not be read
 *  (that read fails closed). Neither is a malfunction, so neither belongs in
 *  the red fault banner — `chat_route.py` splits the two codes for the same
 *  reason, and this is the client half of that split. */
export const POLICY_DECISION_CODES = new Set([
  "policy_blocked",
  "policy_unavailable",
]);
export const isPolicyDecision = (f: Frame): boolean =>
  isError(f) && POLICY_DECISION_CODES.has((f as ErrorFrame).code ?? "");
export const isCitations = (f: Frame): f is BotCitationsFrame =>
  f.kind === FrameKind.BotCitations;
export const isWebSearchResult = (f: Frame): f is WebSearchResultFrame =>
  f.kind === FrameKind.WebSearchResult;
export const isRefusal = (f: Frame): boolean =>
  f.kind === FrameKind.Refusal || f.kind === FrameKind.BotRefusal;
export const isThinking = (f: Frame): f is BotThinkingChunkFrame =>
  f.kind === FrameKind.BotThinkingChunk;
export const isFactsUpdated = (f: Frame): f is FactsUpdatedFrame =>
  f.kind === FrameKind.FactsUpdated;
export const isAvailableCommands = (f: Frame): f is AvailableCommandsFrame =>
  f.kind === FrameKind.AvailableCommands;
export const isDocumentIngest = (f: Frame): f is DocumentIngestFrame =>
  f.kind === FrameKind.DocumentIngest || f.kind === FrameKind.DocumentIngested;

/** Extract a human-friendly tool name from either envelope shape. */
export function toolNameOf(f: Frame): string | undefined {
  if (f.kind === FrameKind.BotToolStatus) return (f as BotToolStatusFrame).tool;
  if (f.kind === FrameKind.BotToolProgress)
    return (f as BotToolProgressFrame).tool;
  if (
    f.kind === FrameKind.ToolStart ||
    f.kind === FrameKind.ToolEnd ||
    f.kind === FrameKind.ToolProgress
  ) {
    return (f as ToolStartFrame).tool_name;
  }
  return undefined;
}
